// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { GetProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DisplayProductsResult } from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import ProductListState, { ProductFilters } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    AttributeTypeFacetModel,
    ProductInventoryDto,
    ProductModel,
    RealTimeInventoryModel,
    RealTimePricingModel,
} from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";
import isEqual from "lodash/isEqual";

interface XProductListStateType {
    filterDrawerIsOpen?: boolean;
    hasGrouping?: boolean;
    productListTableColumns: AttributeTypeFacetModel[];
    levelImagesMap: Dictionary<{ imagePath: string; imageAltText: string }>;
    levelsMap: Dictionary<any>;
    productInfosByProductId: ProductListState["productInfosByProductId"];
    productListProducts: ProductModel[];
    tableProductsParameter?: { [key: string]: GetProductCollectionApiV2Parameter };
    lastExpandedParameter?: string; // used to track the last expanded accordion
    levels: AttributeTypeFacetModel[];
    currentLevel: string;
    previousProductsFilter?: GetProductCollectionApiV2Parameter;
    productListProductsLookup: Dictionary<ProductModel>;
    isProductCarouselLoading: boolean;
    isLoadingLevelProducts: boolean;
    isSendFeedbackModalOpen: boolean;
    productFilters: ProductFilters;
}

const DEFAULT_ACCORDION_TAB_ID = "";

export const initialProductListState: XProductListStateType = {
    filterDrawerIsOpen: false,
    hasGrouping: false,
    productListTableColumns: [],
    levelImagesMap: {},
    levelsMap: {},
    productInfosByProductId: {},
    productListProducts: [],
    productListProductsLookup: {},
    levels: [],
    currentLevel: "",
    tableProductsParameter: {},
    lastExpandedParameter: "",
    isProductCarouselLoading: false,
    isLoadingLevelProducts: false,
    isSendFeedbackModalOpen: false,
    productFilters: {},
};

const reducer = {
    "xProductList/TableView/SetIsSendFeedbackModalOpen": (
        draft: Draft<XProductListStateType>,
        action: { isSendFeedbackModalOpen: boolean },
    ) => {
        draft.isSendFeedbackModalOpen = action.isSendFeedbackModalOpen;
    },
    "xProductList/TableView/SetIsLoadingLevelProducts": (
        draft: Draft<XProductListStateType>,
        action: { isLoadingLevelProducts: boolean },
    ) => {
        draft.isLoadingLevelProducts = action.isLoadingLevelProducts;
    },
    "xProductList/FilterDrawer/SetFilterDrawerIsOpen": (
        draft: Draft<XProductListStateType>,
        action: { filterDrawerIsOpen: boolean },
    ) => {
        draft.filterDrawerIsOpen = action.filterDrawerIsOpen;
    },
    "xProductList/FilterDrawer/CompleteTableGrouping": (
        draft: Draft<XProductListStateType>,
        action: { data: Omit<XProductListStateType & { categoryId?: string }, "filterDrawerIsOpen"> },
    ) => {
        const key = action.data.productFilters?.attributeValueIds?.join("") ?? DEFAULT_ACCORDION_TAB_ID;
        draft.hasGrouping = action.data.hasGrouping;
        draft.productListTableColumns = action.data.productListTableColumns || [];
        draft.levelsMap = action.data.levelsMap || [];
        draft.levels = action.data.levels || [];
        draft.levelImagesMap = action.data.levelImagesMap || {};
        draft.tableProductsParameter = {
            ...draft.tableProductsParameter,
            [key]: action.data.tableProductsParameter?.[key] ?? {}, // or some default/fallback
        };
    },
    "Pages/ProductList/CompleteLoadProducts": (
        draft: Draft<XProductListStateType>,
        action: { result: DisplayProductsResult },
    ) => {
        action.result.products?.forEach(product => {
            draft.productListProductsLookup[product.id] = product;
        });

        const filteredProducts = Object.values(draft.productListProductsLookup);
        draft.productInfosByProductId = { ...draft.productInfosByProductId, ...action.result.productInfosByProductId };

        // if the user selectes from facets, reset the products with the new result
        const { page: currentFilterPage, ...currentFilter } = action.result.filteredApiParameter || {};
        const { page: previousFilterPage, ...previousFilter } = draft.previousProductsFilter || {};
        if (!isEqual(currentFilter, previousFilter)) {
            draft.previousProductsFilter = action.result.filteredApiParameter;
            draft.productListProducts = [];
            draft.productListProductsLookup = {};
            draft.productListProducts = action.result.products || [];
            action.result.products?.forEach(product => {
                draft.productListProductsLookup[product.id] = product;
            });
            return;
        }
        // in case of pagination merge with the new products
        draft.productListProducts = filteredProducts;
    },
    "xProductList/TableView/UpdateProductInfos": (
        draft: Draft<XProductListStateType>,
        action: { productInfosByProductId: Dictionary<ProductInfo> },
    ) => {
        draft.productInfosByProductId = { ...draft.productInfosByProductId, ...action.productInfosByProductId };
    },
    "Pages/ProductList/CompleteLoadRealTimePricing": (
        draft: Draft<XProductListStateType>,
        action: { realTimePricing: RealTimePricingModel },
    ) => {
        action.realTimePricing.realTimePricingResults?.forEach(pricing => {
            const productInfo = draft.productInfosByProductId[pricing.productId];
            if (productInfo) {
                productInfo.pricing = pricing;
                delete productInfo.failedToLoadPricing;
            }
        });
    },
    "Pages/ProductList/FailedLoadRealTimePricing": (
        draft: Draft<XProductListStateType>,
        action: { productId?: string },
    ) => {
        if (action.productId) {
            const productInfo = draft.productInfosByProductId[action.productId];
            if (productInfo) {
                productInfo.failedToLoadPricing = true;
            }
            return;
        }

        for (const productId in draft.productInfosByProductId) {
            draft.productInfosByProductId[productId]!.failedToLoadPricing = true;
        }
    },

    "Pages/ProductList/CompleteLoadRealTimeInventory": (
        draft: Draft<XProductListStateType>,
        action: { realTimeInventory: RealTimeInventoryModel },
    ) => {
        action.realTimeInventory?.realTimeInventoryResults?.forEach((inventory: ProductInventoryDto) => {
            const productInfo = draft.productInfosByProductId[inventory.productId];
            if (productInfo) {
                productInfo.inventory = inventory;
            }
        });
    },
    "Pages/ProductList/FailedLoadRealTimeInventory": (
        draft: Draft<XProductListStateType>,
        action: { productId?: string },
    ) => {
        if (action.productId) {
            const productInfo = draft.productInfosByProductId[action.productId];
            if (productInfo) {
                productInfo.failedToLoadInventory = true;
            }
            return;
        }

        for (const productId in draft.productInfosByProductId) {
            draft.productInfosByProductId[productId]!.failedToLoadInventory = true;
        }
    },
    "Pages/ProductList/ClearProducts": (draft: Draft<XProductListStateType>) => {
        draft.productListProducts = [];
        draft.productListProductsLookup = {};
        draft.productInfosByProductId = {};
        draft.hasGrouping = false;
        draft.productListTableColumns = [];
        draft.levelImagesMap = {};
        draft.levelsMap = {};
        draft.productInfosByProductId = {};
        draft.levels = [];
        draft.currentLevel = "";
    },
    "xProductList/TableView/SetProductsParameter": (
        draft: Draft<XProductListStateType>,
        action: { parameter?: GetProductCollectionApiV2Parameter; currentLevel: string },
    ) => {
        if (action.parameter) {
            const attributeValueIds = action.parameter.attributeValueIds?.join("") ?? DEFAULT_ACCORDION_TAB_ID;
            draft.tableProductsParameter![attributeValueIds] = {
                expand: ["attributes", "facets", "variantTraits", "badges"],
                applyPersonalization: true,
                includeAttributes: ["includeOnProduct"],
                includeSuggestions: true,
                ...action.parameter,
            };
            draft.lastExpandedParameter = attributeValueIds;
        }
        if (action.currentLevel !== undefined) {
            draft.currentLevel = action.currentLevel;
        }
    },
    "xProductList/TableView/UpdateLevelsMap": (
        draft: Draft<XProductListStateType>,
        action: { levelsMap: Dictionary<any> },
    ) => {
        draft.levelsMap = action.levelsMap;
    },
    "xProductList/TableView/CompleteLoadRealTimePricing": (
        draft: Draft<XProductListStateType>,
        action: { realTimePricing: RealTimePricingModel },
    ) => {
        action.realTimePricing.realTimePricingResults?.forEach(pricing => {
            const productInfo = draft.productInfosByProductId[pricing.productId];
            if (productInfo) {
                productInfo.pricing = pricing;
                delete productInfo.failedToLoadPricing;
            }
        });
    },
    "xProductList/TableView/FailedLoadRealTimePricing": (
        draft: Draft<XProductListStateType>,
        action: { productId?: string },
    ) => {
        if (action.productId) {
            const productInfo = draft.productInfosByProductId[action.productId];
            if (productInfo) {
                productInfo.failedToLoadPricing = true;
            }
            return;
        }

        for (const productId in draft.productInfosByProductId) {
            draft.productInfosByProductId[productId]!.failedToLoadPricing = true;
        }
    },

    "xProductList/TableView/CompleteLoadRealTimeInventory": (
        draft: Draft<XProductListStateType>,
        action: { realTimeInventory: RealTimeInventoryModel },
    ) => {
        action.realTimeInventory?.realTimeInventoryResults?.forEach((inventory: ProductInventoryDto) => {
            const productInfo = draft.productInfosByProductId[inventory.productId];
            if (productInfo) {
                productInfo.inventory = inventory;
            }
        });
    },
    "xProductList/TableView/FailedLoadRealTimeInventory": (
        draft: Draft<XProductListStateType>,
        action: { productId?: string },
    ) => {
        if (action.productId) {
            const productInfo = draft.productInfosByProductId[action.productId];
            if (productInfo) {
                productInfo.failedToLoadInventory = true;
            }
            return;
        }

        for (const productId in draft.productInfosByProductId) {
            draft.productInfosByProductId[productId]!.failedToLoadInventory = true;
        }
    },
    "xProductList/ProductCarousel/SetIsLoading": (
        draft: Draft<XProductListStateType>,
        action: { isLoading: boolean },
    ) => {
        draft.isProductCarouselLoading = action.isLoading;
    },
    "Components/ProductInfoLists/CompleteLoadProductInfoList": (draft: Draft<XProductListStateType>) => {
        if (draft.isProductCarouselLoading) {
            draft.isProductCarouselLoading = false;
        }
    },
    "Pages/ProductList/ChangeQtyOrdered": (
        draft: Draft<XProductListStateType>,
        action: { qtyOrdered: number; productId: string },
    ) => {
        const productInfo = draft.productInfosByProductId[action.productId];
        if (productInfo) {
            productInfo.qtyOrdered = action.qtyOrdered;
        }
    },
    "Pages/ProductList/ChangeUnitOfMeasure": (
        draft: Draft<XProductListStateType>,
        action: { unitOfMeasure: string; productId: string },
    ) => {
        const productInfo = draft.productInfosByProductId[action.productId];
        if (productInfo) {
            productInfo.unitOfMeasure = action.unitOfMeasure;
        }
    },
    "Pages/ProductList/SetProductFilters": (
        draft: Draft<XProductListStateType>,
        action: { result: ProductFilters },
    ) => {
        draft.productFilters = action.result;
        draft.currentLevel = "";
    },
};

const xProductList = createTypedReducerWithImmer(initialProductListState, reducer);

(reducers as any).xProductList = xProductList;

export type xProductListState = {
    xProductList: XProductListStateType;
} & ApplicationState;

type xProductListReducers = {
    [key: string]: typeof xProductList;
};

export type xProductListActions = Parameters<xProductListReducers[keyof xProductListReducers]>[1] | AnyAction;
